import { Search, X, Calendar } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { cn } from "../../lib/utils";

const RANGE_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "7d", label: "Last 7 Days" },
  { value: "30d", label: "Last 30 Days" },
  { value: "all", label: "All Completed" },
  { value: "archived", label: "Archived" },
];

/**
 * Top filter card for the History page.
 * Owns no state of its own — purely controlled via callbacks so HistoryPage
 * can keep URL sync and refresh logic centralised.
 *
 * Range dropdown stays fully interactive even when a search query is active
 * (Feb 2026 fix): user complaint — typing a rego locked the dropdown, so
 * widening the search required deleting the rego, changing the range, then
 * re-typing the rego. Now the dropdown always applies. Pick "All Completed"
 * when a narrow window comes back empty.
 *
 * "Archived" is a first-class range option (Feb 2026) — selecting it flips
 * the underlying fetch to `archived_only=true`. Dispatcher can view archived
 * rows read-only via this option; admin/viewer keep full manage actions.
 */
export default function HistoryFilters({
  query, setQuery,
  range, setRange,
  sourceFilter, setSourceFilter,
  loading,
  completedCount, cancelledCount,
}) {
  const archivedRange = range === "archived";

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-md p-3 flex flex-wrap items-center gap-3">
      <div className="relative flex-1 min-w-[240px]">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search rego, vehicle, colour, name, phone, claim #, address, WO#, notes…"
          className="w-full pl-9 pr-9 py-2 bg-slate-50 dark:bg-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="history-search"
        />
        {query && (
          <button
            onClick={() => setQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            aria-label="Clear search"
            data-testid="history-search-clear"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-slate-500" />
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger
            className={cn(
              "w-[180px]",
              archivedRange && "border-violet-400 dark:border-violet-600 bg-violet-50 dark:bg-violet-950/30",
            )}
            data-testid="history-range-select"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value} data-testid={`history-range-${o.value}`}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {sourceFilter && (
          <button
            type="button"
            onClick={() => setSourceFilter("")}
            className="text-[10px] uppercase tracking-wider font-bold text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 px-2 py-1 rounded hover:bg-amber-100 dark:hover:bg-amber-900/50 flex items-center gap-1"
            data-testid="history-source-filter-chip"
            title="Clear source filter"
          >
            Source: {sourceFilter.toUpperCase()}
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      <div className="text-xs text-slate-500 ml-auto">
        {loading ? "Loading…" : (
          <>
            <span className="font-semibold text-emerald-700 dark:text-emerald-300">{completedCount}</span> completed ·{" "}
            <span className="font-semibold text-slate-600 dark:text-slate-300">{cancelledCount}</span> cancelled
          </>
        )}
      </div>
    </div>
  );
}

// Exported so HistoryPage's data fetching can compute since/until from the
// same date logic the dropdown advertises.
export { RANGE_OPTIONS };
